import React from "react";

import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import styled from "@mui/material/styles/styled";

import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import usePagination from "@app/common/hooks/usePagination";
import usePreloadImages from "@app/common/hooks/usePreloadImages";
import ROUTES from "@app/common/routes";
import { withoutQueryParams } from "@app/common/utils";
import Config from "@app/config";

import RuiButton from "@rui/Button";
import Link from "@rui/Link";
import Stack, { Row } from "@rui/Stack";
import Stepper from "@rui/Stepper";
import { Bold } from "@rui/styles";

import Image1 from "./assets/RssLinksTutorial1.png";
import Image2 from "./assets/RssLinksTutorial2.png";
import {
  FeaturePopupContentTitle,
  FeaturePopupContentDescription,
  FeaturePopupHeroContainer,
  FeaturePopupCloseButton,
  FeaturePopupContent,
  FeaturePopupCard,
} from "./styles";

const List = styled("ol")(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(3),
  "& > li:first-of-type": {
    paddingBottom: theme.spacing(1),
  },
}));

const Button = styled(RuiButton)({
  minWidth: "120px",
});

const ShareLink = () => (
  <>
    <FeaturePopupContentTitle>
      Share your Smart Scheduler link
    </FeaturePopupContentTitle>
    <FeaturePopupContentDescription>
      This link makes it simple for people to schedule on your best times. All
      you have to do is:
    </FeaturePopupContentDescription>
    <FeaturePopupContentDescription>
      <List>
        <li>
          <Bold>Copy link:</Bold> Copy your custom URL.
        </li>
        <li>
          <Bold>Share:</Bold> Share the link with others to let them schedule a
          meeting. You can send it via email, text, or embed it on your website.
        </li>
      </List>
    </FeaturePopupContentDescription>
  </>
);

const Customize = () => (
  <>
    <FeaturePopupContentTitle>
      Customize and get easy access
    </FeaturePopupContentTitle>
    <FeaturePopupContentDescription>
      <Bold>Settings:</Bold> Personalize your link, availability, change your
      default calendar, and more in your{" "}
      <Link href={ROUTES.USER_SETTINGS_SMART_SCHEDULER}>account settings</Link>
    </FeaturePopupContentDescription>
    <FeaturePopupContentDescription>
      <Bold>Custom links:</Bold> Navigate to Calendar {">"}{" "}
      <Link href={ROUTES.CALENDAR_SCHEDULING}>Scheduling</Link> to create custom
      links and change your default meeting durations.
    </FeaturePopupContentDescription>
    <FeaturePopupContentDescription>
      <Bold>Extension:</Bold> Want easy access to your link? Install the
      extension for <Link href={Config.rss.CHROME_WEB_STORE_URL}>Chrome</Link>{" "}
      and <Link href={Config.rss.CHROME_WEB_STORE_URL}>Edge</Link>.
    </FeaturePopupContentDescription>
  </>
);

const PAGES = [
  { content: <ShareLink />, image: Image1 },
  { content: <Customize />, image: Image2 },
];

const SmartScheduleFirstTimeUseTutorial = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  usePreloadImages([Image1, Image2]);

  const { goToPage, goToNextPage, hasNext, currentPage } = usePagination(
    PAGES,
    {
      pageSize: 1,
    }
  );

  const page = PAGES[currentPage];

  return (
    <Modal open onClose={onClose} disableAutoFocus>
      <FeaturePopupCard>
        <FeaturePopupHeroContainer height="200px">
          <Box component="img" src={page.image} />
          <FeaturePopupCloseButton onClick={onClose} />
        </FeaturePopupHeroContainer>
        <FeaturePopupContent spacing={1}>
          <Stack spacing={1}>{page.content}</Stack>
          <Row variant="apart">
            <Stepper
              activeStep={currentPage}
              onClickStep={goToPage}
              steps={PAGES.length}
              activeStepColor="shades.primary.500"
              inactiveStepColor="shades.neutral.100"
            />
            {hasNext ? (
              <Button onClick={goToNextPage}>Next</Button>
            ) : (
              <Button onClick={onClose}>Got it!</Button>
            )}
          </Row>
        </FeaturePopupContent>
      </FeaturePopupCard>
    </Modal>
  );
};

const SmartScheduleFirstTimeTutorialGate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const { userMetadata, updateUserMetadata } = useUserSettings();

  const shouldShow = queryParams.get("popup") === "rss_tutorial";

  if (!shouldShow || userMetadata?.smartSchedulerTutorialComplete) {
    return null;
  }

  const handleClose = () => {
    navigate(
      withoutQueryParams(location.pathname, location.search, ["popup"]),
      { replace: true }
    );
    updateUserMetadata.mutate({ smartSchedulerTutorialComplete: true });
  };

  return <SmartScheduleFirstTimeUseTutorial onClose={handleClose} />;
};

export default SmartScheduleFirstTimeTutorialGate;
